<template>
    <article class="filter-section" :class="'filter-section-' + dictionary.filterType">
        <div class="enabled-form">
            <input class="enabled-checkbox" @change="handleCheckboxChange" :id="checkboxId" type="checkbox" :checked="enabled" :aria-checked="enabled" />
            <label class="enabled-checkbox-label" :for="checkboxId">Filter {{ dictionary.filterType }} items</label>
        </div>
        <p class="instructions">
            Use <a :href="dictionary.language.docs">{{ dictionary.language.name }} ({{ dictionary.language.abbreviation }})</a>
        </p>
        <div class="query-form">
            <label class="query-label" :for="textareaId">{{ dictionary.language.abbreviation }} query</label>
            <textarea
                class="query-textarea"
                @input="handleQueryChange($event)"
                spellcheck="false"
                :id="textareaId"
                :disabled="!enabled"
                :aria-disabled="!enabled"
                :value="query">
            </textarea>
            <p class="query-appendix" v-if="appendix !== undefined">
                <icon class="query-appendix-icon" name="plus-thin" label="The following query will be appended to the final value:" />
                <span class="query-appendix-value">{{ appendix }}</span>
            </p>
        </div>
        <div class="controls">
            <div class="test-query">
                <button @click="handleTestQueryClick" class="test-query-button" type="button" :disabled="!enabled" :aria-disabled="!enabled">Test query</button>
                <div class="test-query-result" v-if="testResult !== undefined">
                    <p class="test-query-text" v-if="testResult.success">
                        {{ testResult.count }} item(s) found!
                        <a :href="testResult.url" target="_blank">See in {{ dictionary.filterType }}</a>
                    </p>
                    <p class="test-query-text" v-if="!testResult.success">
                        Query failed!
                        <button class="test-query-details-button" type="button" @click="handleQueryDetailsClick">See details</button>
                    </p>
                </div>
            </div>
            <a class="see-examples" href="https://apps.appf.re/t4j/doc/sh/filters" target="_blank">
                <icon class="see-examples-icon" name="open-in-new-tab" />
                <span class="see-examples-text">See examples</span>
            </a>
        </div>
    </article>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
    props: {
        dictionary: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters('profileFilters', [
            'jiraFilteringEnabled',
            'azureFilteringEnabled',
            'jqlFilterText',
            'jqlAppendix',
            'wiqlFilterText',
            'wiqlAppendix',
            'jqlTestResult',
            'wiqlTestResult',
        ]),
        enabled() {
            switch (this.dictionary.filterType) {
                case "Jira": return this.jiraFilteringEnabled;
                case "TFS": return this.azureFilteringEnabled;
                default: return false;
            }
        },
        query() {
            switch (this.dictionary.filterType) {
                case "Jira": return this.jqlFilterText;
                case "TFS": return this.wiqlFilterText;
                default: return '';
            }
        },
        appendix() {
            switch (this.dictionary.filterType) {
                case "Jira": return this.jqlAppendix;
                case "TFS": return this.wiqlAppendix;
                default: return undefined;
            }
        },
        testResult() {
            switch (this.dictionary.filterType) {
                case "Jira": return this.jqlTestResult;
                case "TFS": return this.wiqlTestResult;
                default: return undefined;
            }
        },
        checkboxId() {
            return 'filter-enabled-' + this.dictionary.filterType;
        },
        textareaId() {
            return 'filter-query-' + this.dictionary.filterType;
        },
    },
    methods: {
        ...mapMutations('profileFilters', [
            'toggleJiraFiltering',
            'toggleAzureFiltering',
            'setJqlQuery',
            'setWiqlQuery',
        ]),
        ...mapActions('profileFilters', [
            'testJqlQuery',
            'testWiqlQuery',
            'showPopup',
        ]),
        handleCheckboxChange: function () {
            switch (this.dictionary.filterType) {
                case "Jira":
                    this.toggleJiraFiltering();
                    break;
                case "TFS":
                    this.toggleAzureFiltering();
                    break;
            }
        },
        handleQueryChange: function (event) {
            const query = event.target.value;

            switch (this.dictionary.filterType) {
                case "Jira":
                    this.setJqlQuery(query);
                    break;
                case "TFS":
                    this.setWiqlQuery(query);
                    break;
            }
        },
        handleTestQueryClick: function () {
            switch (this.dictionary.filterType) {
                case "Jira":
                    this.testJqlQuery(this.query);
                    break;
                case "TFS":
                    this.testWiqlQuery(this.query);
                    break;
            }
        },
        handleQueryDetailsClick: function () {
            this.showPopup({
                title: 'Query details',
                message: this.testResult.reason,
            });
        },
    },
};
</script>
<style scoped>
.enabled-form {
    display: flex;
    align-items: center;
    column-gap: 10px;
}

.enabled-checkbox {
    margin: 0;
}

.enabled-checkbox-label {
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
}

.instructions {
    margin-top: 10px;
}

.query-form {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
}

.query-label {
    font-size: 12px;
    color: #767676;
}

.query-textarea {
    resize: none;
    border: 1px solid #ccc;
    font-family: sans-serif;
    padding: 8px;
    box-sizing: border-box;
}

.query-appendix {
    display: flex;
    align-items: center;
    column-gap: 5px;
    margin: 0;
}

.query-appendix-icon {
    flex-shrink: 0;
    fill: #767676;
}

.query-appendix-value {
    font-size: 12px;
    color: #767676;
}

.controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 25px;
}

.test-query {
    display: flex;
    align-items: center;
    column-gap: 15px;
}

.test-query-result {
    display: flex;
}

.test-query-text {
    margin: 0;
}

.test-query-details-button {
    padding: 0;
    border: 0;
    background: 0;
    cursor: pointer;
    color: #069;
}

.test-query-details-button:hover {
    text-decoration: underline;
}

.see-examples {
    display: flex;
    align-items: center;
    column-gap: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/profile/filters/ProfileFilterSection.vue